import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Building2, Landmark, BookOpen } from "lucide-react";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar";
import { replaceFtsMatch } from "Explore/Utils/replaceFtsMatch";

const stripFts = (str) => {
  if (typeof str !== "string") return str || "";
  return str.replace(/<\/?(?:fts_match|span)[^>]*>/gi, "");
};

const getInitials = (name) => {
  const cleanName = stripFts(name);
  if (!cleanName) return "U";
  return cleanName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");
};

const formatNum = (val) => {
  if (val === undefined || val === null || val === "") return "-";
  return Number(val).toLocaleString();
};

const CollectionTableView = ({ items = [], entityType = "researchers" }) => {
  if (!items.length) {
    return (
      <div className="py-12 text-center text-gray-500 text-sm">
        No items in this collection.
      </div>
    );
  }

  const getItemLink = (item) => {
    const slug = item.explore_slug || item.id;
    if (entityType === "units") return `/explore/unit/${slug}`;
    if (entityType === "institutions") return `/explore/institution/${slug}`;
    if (entityType === "venues") return `/explore/venue/${slug}`;
    return `/explore/faculty/${slug}`;
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-xs">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50/70 border-b border-gray-200 text-xs">
            <TableHead className="w-[8%] text-center">#</TableHead>
            <TableHead className="w-[42%] font-semibold text-gray-700">
              {entityType === "units"
                ? "Unit Name"
                : entityType === "institutions"
                ? "Institution Name"
                : entityType === "venues"
                ? "Venue Name"
                : "Researcher Name"}
            </TableHead>
            <TableHead className="font-semibold text-gray-700">
              {entityType === "institutions" ? "Location" : entityType === "venues" ? "Type" : "Affiliation"}
            </TableHead>
            <TableHead className="text-right font-semibold text-gray-700">
              {entityType === "institutions" ? "Faculties" : "H-Index"}
            </TableHead>
            <TableHead className="text-right font-semibold text-gray-700">
              Citations
            </TableHead>
            <TableHead className="text-right font-semibold text-gray-700">
              Publications
            </TableHead>
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {items.map((item, index) => {
            const link = getItemLink(item);
            const avatarUrl = item.photo_url || item.photo || item.logo_url || null;
            const hIndex = item.h_index ?? item.total_h_index ?? item.hIndex;
            const citations = item.citation_count ?? item.total_citation ?? item.citations;
            const publications = item.publication_count ?? item.total_publication ?? item.publications;
            const faculties = item.total_faculties ?? item.faculties;

            let subInfo = "";
            if (entityType === "units") {
              subInfo = item.institution_name || "";
            } else if (entityType === "institutions") {
              subInfo = [item.city, item.state].filter(Boolean).join(", ");
            } else if (entityType === "venues") {
              subInfo = Array.isArray(item.keywords) ? item.keywords.slice(0, 2).map(k => typeof k === 'object' ? k.value : k).join(", ") : "Venue";
            } else {
              subInfo = item.institution?.name || item.institution_name || item.position || "";
            }

            return (
              <TableRow key={item.id || index} className="hover:bg-gray-50/60 text-xs transition-colors">
                <TableCell className="text-center font-medium text-gray-400">
                  {index + 1}
                </TableCell>

                <TableCell>
                  <div className="flex items-center gap-2.5">
                    <Avatar className="h-7 w-7 border border-gray-200 flex-shrink-0">
                      {avatarUrl && <AvatarImage src={avatarUrl} alt={stripFts(item.name)} />}
                      <AvatarFallback className="bg-explore/10 text-explore text-[10px] font-semibold">
                        {entityType === "researchers" ? (
                          getInitials(item.name)
                        ) : entityType === "units" ? (
                          <Building2 size={13} />
                        ) : entityType === "venues" ? (
                          <BookOpen size={13} />
                        ) : (
                          <Landmark size={13} />
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <Link
                        to={link}
                        className="font-medium text-gray-900 hover:text-explore block truncate"
                        title={stripFts(item.name)}
                      >
                        <span
                          dangerouslySetInnerHTML={{
                            __html: replaceFtsMatch(item.name || ""),
                          }}
                        />
                      </Link>
                    </div>
                  </div>
                </TableCell>

                <TableCell
                  className="text-gray-500 truncate max-w-[180px]"
                  title={stripFts(subInfo)}
                >
                  {subInfo ? (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: replaceFtsMatch(subInfo),
                      }}
                    />
                  ) : (
                    "-"
                  )}
                </TableCell>

                <TableCell className="text-right font-medium text-gray-900">
                  {entityType === "institutions" ? formatNum(faculties) : formatNum(hIndex)}
                </TableCell>

                <TableCell className="text-right text-gray-600">
                  {formatNum(citations)}
                </TableCell>

                <TableCell className="text-right text-gray-600">
                  {formatNum(publications)}
                </TableCell>

                <TableCell className="text-right">
                  <Link
                    to={link}
                    className="inline-flex p-1 text-gray-400 hover:text-explore rounded transition-colors"
                    title="View Profile"
                  >
                    <ExternalLink size={13} />
                  </Link>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default CollectionTableView;
